/** Período da tela "Meus alertas" (fica no endereço: ?p=7d|90d|outro&de=&ate=). */
import { diaBr, diaDoIso, diasAtras, isoDoDia } from '@/features/meus-alertas/dados'

export type Periodo = '7d' | '30d' | '90d' | 'outro'

export const PERIODOS: readonly { id: Periodo; curto: string; dias?: number }[] = [
  { id: '7d', curto: '7 dias', dias: 7 },
  { id: '30d', curto: '30 dias', dias: 30 },
  { id: '90d', curto: '90 dias', dias: 90 },
  { id: 'outro', curto: 'Outro' },
]

export type BuscaMeusAlertas = { p?: Periodo; de?: string; ate?: string }

const ISO_DIA = /^\d{4}-\d{2}-\d{2}$/

/** Lê os filtros do endereço, ignorando valores que não fazem sentido. */
export function lerBuscaMeusAlertas(busca: Record<string, unknown>): BuscaMeusAlertas {
  const p = PERIODOS.find((item) => item.id === busca.p)?.id
  const de = typeof busca.de === 'string' && ISO_DIA.test(busca.de) ? busca.de : undefined
  const ate = typeof busca.ate === 'string' && ISO_DIA.test(busca.ate) ? busca.ate : undefined
  return { p, de, ate }
}

/** Datas da consulta. Padrão: últimos 30 dias. */
export function intervaloDaBusca(busca: BuscaMeusAlertas, hoje: string) {
  const periodo = busca.p ?? '30d'
  const fixo = PERIODOS.find((item) => item.id === periodo)?.dias
  if (fixo !== undefined) return { periodo, inicio: diasAtras(fixo), fim: hoje }
  const inicio = busca.de && busca.de <= hoje ? busca.de : diasAtras(30)
  const fim = busca.ate && busca.ate >= inicio ? busca.ate : hoje
  return { periodo, inicio, fim }
}

export type Intervalo = ReturnType<typeof intervaloDaBusca>

/**
 * Todos os dias (ou semanas, começando na segunda) do período, cada um com o
 * valor que `valorDoDia` devolver. Assim o eixo do tempo não pula dias.
 */
function diasDoPeriodo<T>(
  inicio: string,
  fim: string,
  granularidade: 'day' | 'week',
  valorDoDia: (iso: string) => T
): ({ dia: string } & T)[] {
  const atual = diaDoIso(inicio)
  if (granularidade === 'week') atual.setDate(atual.getDate() - ((atual.getDay() + 6) % 7))
  const ultimo = diaDoIso(fim)
  const passo = granularidade === 'week' ? 7 : 1
  const saida: ({ dia: string } & T)[] = []
  while (atual <= ultimo && saida.length < 400) {
    const iso = isoDoDia(atual)
    saida.push({ dia: diaBr(iso).slice(0, 5), ...valorDoDia(iso) })
    atual.setDate(atual.getDate() + passo)
  }
  return saida
}

/** Alertas por dia (ou semana) do período, com zero onde a API não trouxe ponto. */
export function serieCompleta(
  pontos: { period: string; count: number }[],
  inicio: string,
  fim: string,
  granularidade: 'day' | 'week'
): { dia: string; alertas: number }[] {
  const porDia = new Map(pontos.map((p) => [p.period.slice(0, 10), p.count]))
  return diasDoPeriodo(inicio, fim, granularidade, (iso) => ({ alertas: porDia.get(iso) ?? 0 }))
}

/** Eventos com e sem alerta por dia (ou semana), com zero onde a API não trouxe ponto. */
export function serieEventosCompleta(
  pontos: { period: string; events_with_violations: number; events_without_violations: number }[],
  inicio: string,
  fim: string,
  granularidade: 'day' | 'week'
): { dia: string; sem: number; com: number }[] {
  const porDia = new Map(pontos.map((p) => [p.period.slice(0, 10), p]))
  return diasDoPeriodo(inicio, fim, granularidade, (iso) => {
    const ponto = porDia.get(iso)
    return {
      sem: ponto?.events_without_violations ?? 0,
      com: ponto?.events_with_violations ?? 0,
    }
  })
}
